import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { generateId } from '@frontastic/common';
import classnames from 'classnames';
import { bindColors } from 'helpers/productHelpers';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { useSelector } from 'react-redux';
import { SwiperSlide } from 'swiper/react';
import OldTagManager from '../../../../domain/OldTagManager';
import tagManager from '../../../../domain/TagManager';
import ProductColorService from '../../../../services/color';
import GtmService from '../../../../services/gtm';
import OldGtmService from '../../../../services/oldGtm';
import { Product } from '../../../../types/algolia/product';
import { Context } from '../../../../types/frontastic';
import Slider from '../../../atoms/slider';
import ProductTile from '../Hits/ProductTile';
import useConsentManagement from 'anwr_sport2000/src/js/services/hook/useConsentManagement';

type Props = {
  context?: Context;
  objectId: string | null;
  gtmCategoryName: string;
  gtmCategoryId: string;
  renderAsSlider: boolean;
  swiperColor: string;
  alignment: string;
  hits: Array<Product>;
  isDisplayOnPDP?: boolean;
  refererStore: any;
} & WrappedComponentProps;

const ProductRecommendationTile: React.FC<Props> = ({
  gtmCategoryName,
  context,
  hits,
  refererStore,
  renderAsSlider,
  swiperColor,
  alignment,
  gtmCategoryId,
  isDisplayOnPDP,
}) => {
  const [colors, setColors] = useState<Record<string, any>>({});
  const observer = useRef<IntersectionObserver | null>(null);
  const wishlistState = useSelector((state) => (state as any)?.wishlist?.wishlist?.data?.lineItems || []);
  const { isGtmAccepted } = useConsentManagement();
  const [isProductImpressTracked, setIsProductImpressTracked] = useState(false);

  useEffect(() => {
    if (isGtmAccepted && hits?.length > 0 && !isProductImpressTracked) {
      // GTM services initialization
      const gtmService = new GtmService(hits);
      const productListGTM = gtmService.createProductListGTM(gtmCategoryName, gtmCategoryId);
      tagManager.productImpressions(productListGTM, gtmCategoryName, gtmCategoryId);

      const oldGtmService = new OldGtmService(hits);
      const oldProductListGTM = oldGtmService.createProductListGTM(`Page: ${gtmCategoryName}`);
      OldTagManager.productImpressions(oldProductListGTM);
      setIsProductImpressTracked(true)
    }
  }, [gtmCategoryName, gtmCategoryId, hits, isGtmAccepted, isProductImpressTracked]);

  useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const modelKeys: string[] = [];

    hits.forEach((hit) => {
      if (!colors[hit.root_model_key]) {
        colors[hit.root_model_key] = [hit];
        modelKeys.push(hit.root_model_key);
      }
    });

    if (context && modelKeys.length > 0) {
      ProductColorService.getColors(modelKeys).then((responseColors) => {
        const updatedColors = { ...colors };
        responseColors?.forEach((hitColor: any) => {
          const hitColors = updatedColors[hitColor.root_model_key] ?? [];
          if (!hitColors.some((hit) => hit.product_id === hitColor.product_id)) {
            hitColors.push(hitColor);
          }
          updatedColors[hitColor.root_model_key] = hitColors;
        });
        setColors(updatedColors);
      });
    }
  }, [hits, colors]);

  // const bindColors = useCallback((hits: Product[], colors: Record<string, any>) => {
  //   if (!colors || hits?.length === 0) {
  //     return hits;
  //   }

  //   return hits.map((hit) => {
  //     if (hit.root_model_key) {
  //       const allColorsOfProduct = colors[hit.root_model_key] ?? [];
  //       hit.colors = allColorsOfProduct.map((item) => ({
  //         ...item,
  //         isActive: item.sku === hit.sku,
  //       }));
  //     }
  //     return hit;
  //   });
  // }, []);

  const hitsWithColors = useMemo(
    () => bindColors({ hits, colors, wishlist: wishlistState, store: refererStore }),
    [hits, colors, wishlistState, refererStore],
  );

  const renderProductTilesAsList = () => {
    return hitsWithColors.map((hit, index) => (
      <ProductTile
        key={`ProductRecommendationTile_${generateId()}_${hit.product_id}_${index}`}
        position={index + 1}
        product={hit}
        nameCategory={gtmCategoryName}
        categoryId={gtmCategoryId}
        refererStore={refererStore}
        colors={hit.colors as any}
        from="Related Products"
      />
    ));
  };

  const renderProductTilesAsSlider = () => {
    return (
      <Slider
        spaceBetween={0}
        slidesPerView={2}
        slidesPerGroup={2}
        loop={false}
        swiperColor={swiperColor}
        breakpoints={{
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            spaceBetween: 12,
          },
          1024: {
            slidesPerView: isDisplayOnPDP ? 3 : 5,
            slidesPerGroup: isDisplayOnPDP ? 3 : 5,
            spaceBetween: isDisplayOnPDP ? 16 : 20,
          },
          1280: {
            slidesPerView: isDisplayOnPDP ? 4 : 5,
            slidesPerGroup: isDisplayOnPDP ? 4 : 5,
            spaceBetween: isDisplayOnPDP ? 16 : 20,
          },
        }}
      >
        {hitsWithColors.map((hit, index) => (
          <SwiperSlide key={`ProductRecommendationTile_${hit.product_id}`}>
            <ProductTile
              key={`ProductRecommendationTile_${hit.product_id}_${index}`}
              position={index + 1}
              product={hit}
              nameCategory={gtmCategoryName}
              categoryId={gtmCategoryId}
              refererStore={refererStore}
              colors={hit.colors as any}
              from="Related Products"
            />
          </SwiperSlide>
        ))}
      </Slider>
    );
  };

  if (!hits || hits.length === 0) {
    return null;
  }

  return (
    <div className="product-list--component">
      {renderAsSlider === false ? (
        <div
          className={classnames('product-teaser-list', {
            'justify-start': alignment === 'left',
            'justify-center': alignment === 'center',
            'justify-end': alignment === 'right',
          })}
        >
          {renderProductTilesAsList()}
        </div>
      ) : (
        renderProductTilesAsSlider()
      )}
    </div>
  );
};

export default injectIntl(ProductRecommendationTile);
